import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { FaSearch } from 'react-icons/fa';
import { API_BASE, parseJsonResponse, getUserErrorMessage } from '../../utils/apiBase';
import { authFetch } from '../../utils/apiAuth';
import SuperPanelShell from '../../components/super/SuperPanelShell';
import './superPanel.css';

function accountId(account) {
  return String(account?.id || account?._id || '');
}

export default function ShopAccounts() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [togglingId, setTogglingId] = useState('');

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API_BASE}/v1/shop/accounts`);
      const result = await parseJsonResponse(response);
      setAccounts(result.data || []);
    } catch (error) {
      setAccounts([]);
      toast.error(getUserErrorMessage(error, 'Unable to load shop accounts.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const filtered = accounts.filter((account) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      account.shopName?.toLowerCase().includes(q) ||
      account.city?.toLowerCase().includes(q) ||
      account.name?.toLowerCase().includes(q) ||
      account.email?.toLowerCase().includes(q) ||
      account.phone?.toLowerCase().includes(q) ||
      account.password?.toLowerCase().includes(q)
    );
  });

  const activeCount = accounts.filter((a) => a.isActive).length;
  const cityCount = new Set(accounts.map((a) => a.city).filter(Boolean)).size;

  const handleToggleActive = async (account) => {
    const id = accountId(account);
    const makeActive = !account.isActive;
    const action = makeActive ? 'activate' : 'deactivate';

    if (!window.confirm(`${makeActive ? 'Activate' : 'Deactivate'} shop "${account.shopName}"?`)) return;

    setTogglingId(id);
    try {
      const response = await authFetch(`${API_BASE}/v1/shop/accounts/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: makeActive }),
      });
      await parseJsonResponse(response);
      toast.success(`Shop ${action}d`);
      fetchAccounts();
    } catch (error) {
      toast.error(getUserErrorMessage(error, `Unable to ${action} shop.`));
    } finally {
      setTogglingId('');
    }
  };

  return (
    <SuperPanelShell
      title="Shop Accounts"
      titleExtra={<span className="sp-count-pill">{accounts.length} shops</span>}
      breadcrumb="Super Panel • Shop Panel Logins"
      action={
        <button type="button" className="sp-btn sp-btn--gold" onClick={() => navigate('/super/shops/add')}>
          + Create Shop Login
        </button>
      }
    >
      <Toaster
        position="top-center"
        toastOptions={{
          style: { background: '#12121f', color: '#f5d76e', border: '1px solid rgba(212,175,55,0.3)' },
        }}
      />

      <div className="sp-stats-row">
        <div className="sp-stat-card">
          <span className="sp-stat-label">Total Shops</span>
          <strong className="sp-stat-value">{accounts.length}</strong>
          <span className="sp-stat-hint">POS login accounts</span>
        </div>
        <div className="sp-stat-card">
          <span className="sp-stat-label">Active</span>
          <strong className="sp-stat-value sp-stat-gold">{activeCount}</strong>
          <span className="sp-stat-hint">Can access shop panel</span>
        </div>
        <div className="sp-stat-card">
          <span className="sp-stat-label">Cities</span>
          <strong className="sp-stat-value">{cityCount}</strong>
          <span className="sp-stat-hint">Unique locations</span>
        </div>
      </div>

      <div className="sp-card">
        <div className="sp-card-toolbar">
          <div className="sp-search">
            <FaSearch />
            <input
              type="search"
              placeholder="Search shop, city, email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoComplete="off"
            />
          </div>
        </div>

        <div className="sp-card-body">
          {loading ? (
            <p className="sp-muted">Loading shop accounts...</p>
          ) : filtered.length === 0 ? (
            <p className="sp-muted">No shop accounts found.</p>
          ) : (
            <div className="sp-table-wrap">
              <table className="sp-table">
                <thead>
                  <tr>
                    <th>Shop</th>
                    <th>City</th>
                    <th>Contact</th>
                    <th>Login Email</th>
                    <th>Password</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((account) => {
                    const id = accountId(account);
                    return (
                      <tr key={id || account.email}>
                        <td>
                          <button
                            type="button"
                            className="sp-shop-link"
                            onClick={() => navigate(`/super/shops/view/${id}`)}
                          >
                            {account.shopName}
                          </button>
                        </td>
                        <td>{account.city}</td>
                        <td>{account.name}</td>
                        <td>{account.email}</td>
                        <td>
                          <code style={{ color: '#f5d76e', fontSize: 12 }}>{account.password || '—'}</code>
                        </td>
                        <td>
                          <span className={`sp-role-badge ${account.isActive ? 'sp-role-admin' : 'sp-role-suspended'}`}>
                            {account.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>
                          <div className="sp-actions">
                            <button
                              type="button"
                              className="sp-btn sp-btn--ghost sp-btn--sm"
                              onClick={() => navigate(`/super/shops/edit/${id}`)}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className={`sp-btn sp-btn--sm ${account.isActive ? 'sp-btn--danger' : 'sp-btn--gold'}`}
                              disabled={togglingId === id}
                              onClick={() => handleToggleActive({ ...account, id })}
                            >
                              {togglingId === id
                                ? 'Saving...'
                                : account.isActive
                                  ? 'Deactivate'
                                  : 'Activate'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </SuperPanelShell>
  );
}
